import { EXCHANGE_BRAND_USER_TYPES, EXCHANGE_BRAND_ENTITY_TYPES } from "../exchangeBrands";
import { ROLES, PERMISSIONS } from "../users";
const invertMap = (obj) => new Map(Object.entries(obj).map(([k, v]) => [v, k]));
const roleMap = invertMap(ROLES);
const permissionMap = invertMap(PERMISSIONS);
const brandUserTypeMap = invertMap(EXCHANGE_BRAND_USER_TYPES);
const brandEntityTypeMap = invertMap(EXCHANGE_BRAND_ENTITY_TYPES);
const getRoleStr = (role) => { var _a; return (_a = roleMap.get(role)) !== null && _a !== void 0 ? _a : 'UNKNOWN'; };
const getPermissionStr = (permission) => { var _a; return (_a = permissionMap.get(permission)) !== null && _a !== void 0 ? _a : 'UNKNOWN'; };
const getBrandUserTypeStr = (userType) => { var _a; return (_a = brandUserTypeMap.get(userType)) !== null && _a !== void 0 ? _a : 'UNKNOWN'; };
const getBrandEntityTypeStr = (entityType) => { var _a; return (_a = brandEntityTypeMap.get(entityType)) !== null && _a !== void 0 ? _a : 'UNKNOWN'; };
// Reusable permission rules
export const PermissionRules = {
    // Role-based rules
    hasRole: (role) => ({
        check: (profile) => profile.role_id === role,
        errorMessage: `Requires role: ${getRoleStr(role)}`,
    }),
    hasAnyRole: (roles) => ({
        check: (profile) => roles.includes(profile.role_id),
        errorMessage: `Requires one of roles: ${roles.map(getRoleStr).join(', ')}`,
    }),
    // Team User-specific rules
    hasTeamPermission: (permission) => ({
        check: (profile) => {
            if (profile.role_id !== ROLES.TEAM_USER) {
                return false;
            }
            const permissions = profile.academic_profile.group.permissions.map((p) => p.id);
            return permissions.includes(permission);
        },
        errorMessage: `Requires permission: ${getPermissionStr(permission)}`,
    }),
    hasAllTeamPermissions: (permissions) => ({
        check: (profile) => {
            if (profile.role_id !== ROLES.TEAM_USER) {
                return false;
            }
            const profilePermissions = profile.academic_profile.group.permissions.map((p) => p.id);
            return permissions.every((p) => profilePermissions.includes(p));
        },
        errorMessage: `Requires all permissions: ${permissions.map(getPermissionStr).join(', ')}`,
    }),
    hasAnyTeamPermission: (permissions) => ({
        check: (profile) => {
            if (profile.role_id !== ROLES.TEAM_USER) {
                return false;
            }
            const profilePermissions = profile.academic_profile.group.permissions.map((p) => p.id);
            return permissions.some((p) => profilePermissions.includes(p));
        },
        errorMessage: `Requires any of permissions: ${permissions.map(getPermissionStr).join(', ')}`,
    }),
    // Brand-specific rules
    isBrandUserType: (brandUserType) => ({
        check: (profile) => {
            if (profile.role_id !== ROLES.BRAND_USER) {
                return false;
            }
            return profile.exchange_brand_user_type === brandUserType;
        },
        errorMessage: `Requires brand user type: ${getBrandUserTypeStr(brandUserType)}`,
    }),
    isBrandEntityType: (entityType) => ({
        check: (profile) => {
            if (profile.role_id !== ROLES.BRAND_USER) {
                return false;
            }
            return profile.exchange_brand.entity_type === entityType;
        },
        errorMessage: `Requires brand entity type: ${getBrandEntityTypeStr(entityType)}`,
    }),
    // Composite rules
    and: (...rules) => ({
        check: (profile) => rules.every((rule) => rule.check(profile)),
        errorMessage: `(${rules.map(r => r.errorMessage).join(' AND ')})`,
    }),
    or: (...rules) => ({
        check: (profile) => rules.some((rule) => rule.check(profile)),
        errorMessage: `(${rules.map(r => r.errorMessage).join(' OR ')})`,
    }),
    // Custom rule factory
    custom: (check, errorMessage) => ({
        check,
        errorMessage: errorMessage || 'Does not meet permission requirements',
    }),
};
